import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
/**
 * `yp_settings` row for one client — branding, public-registration and
 * invite-URL configuration the add-client wizard's company-information
 * step upserts. `yp_settings` also carries a global (`client_id IS NULL`)
 * row for the platform default, which this contract does not expose: every
 * intent here is addressed by client id.
 */
export const ClientSettingsRow = S.Struct({
    id: S.Int,
    client_id: S.UUID,
    project_name: S.String,
    project_logo: S.NullOr(S.String),
    project_url: S.NullOr(S.String),
    project_color: S.NullOr(S.String),
    project_from_email: S.NullOr(S.String),
    default_language: S.String,
    public_registration: S.Boolean,
    public_registration_verify_email: S.Boolean,
    public_registration_role: S.NullOr(S.UUID),
    storage_default_folder: S.NullOr(S.UUID),
});
export const GetClientSettingsParams = S.Struct({ client_id: S.UUID });
/** `null` when the client has never had its settings row created yet. */
export const GetClientSettings = HttpApiEndpoint.get("getClientSettings", "/admin/settings/get")
    .setUrlParams(GetClientSettingsParams)
    .addSuccess(S.Struct({ data: S.NullOr(ClientSettingsRow) }))
    .addError(Forbidden, { status: 403 });
/**
 * Upserts by `client_id` — the wizard always calls this once per save,
 * whether or not the row exists yet, exactly like the legacy
 * `upsertYpSettings` helper it replaces.
 */
export const UpsertClientSettingsPayload = S.Struct({
    client_id: S.UUID,
    project_name: S.optional(S.NonEmptyString),
    project_logo: S.optional(S.NullOr(S.String)),
    project_url: S.optional(S.NullOr(S.String)),
    project_color: S.optional(S.NullOr(S.String)),
    project_from_email: S.optional(S.NullOr(S.String)),
    default_language: S.optional(S.String),
    public_registration: S.optional(S.Boolean),
    public_registration_verify_email: S.optional(S.Boolean),
    public_registration_role: S.optional(S.NullOr(S.UUID)),
    storage_default_folder: S.optional(S.NullOr(S.UUID)),
});
export const UpsertClientSettings = HttpApiEndpoint.post("upsertClientSettings", "/admin/settings/upsert")
    .setPayload(UpsertClientSettingsPayload)
    .addSuccess(S.Struct({ data: ClientSettingsRow }))
    .addError(Forbidden, { status: 403 });
/**
 * A `report_designs` catalog row — the company-information screen's PDF
 * report-template picker. `preview_file_id` (nullable) references a
 * `yp_files` row fetched through the existing `admin-files.downloadAdminFile`
 * intent; this contract does not duplicate that read path.
 */
export const ReportDesignRow = S.Struct({
    id: S.UUID,
    name: S.String,
    description: S.String,
    design_identifier: S.String,
    preview_file_id: S.NullOr(S.UUID),
    is_default: S.Boolean,
});
/**
 * Every `active` design, ordered by name — the only filter/sort the picker
 * needs; report-design authoring itself has no screen yet.
 */
export const ListReportDesigns = HttpApiEndpoint.get("listReportDesigns", "/admin/report-designs").addSuccess(S.Struct({ items: S.Array(ReportDesignRow), total: S.Int }));
export const adminSettingsGroup = HttpApiGroup.make("admin-settings")
    .add(GetClientSettings)
    .add(UpsertClientSettings)
    .add(ListReportDesigns)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=admin-settings.js.map